// 차트 최상단 단지 비교 표 — 항목: 단지명/지역(생활권)/급지/연식/세대수/평형/적현율/투자금/최고가/적고가/
//   매매가/전세가/매매실거래가/전세실거래가/전세가율/매매 매물수/전세 매물수/공급율
//   적현율=jeokHyunRate(%), 적고가=properHighPrice(만원). 공급율만 값 출처 미정 → '준비중'.
import React from 'react';
import { toNumber, formatNumber, formatKrwEokFromManwon } from './utils';

// 만원 단위 → 한글 억 표기 (소수 1자리, '12.5억' / '13억' / '5,000만')
const fmtEok = formatKrwEokFromManwon;

// 사용승인일(YYYYMMDD/…) → "YYYY (n년차)"
const ageLabel = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length < 4) return '-';
  const year = Number(digits.slice(0, 4));
  if (!Number.isFinite(year) || year < 1900) return '-';
  const age = new Date().getFullYear() - year;
  return `${year} (${age}년차)`;
};

// 최신 스냅샷에서 지표별 마지막 유효값 추출
const pickLatest = (rows, keys) => {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    for (const k of keys) {
      const v = toNumber(rows[i]?.[k]);
      if (v != null) return v;
    }
  }
  return null;
};

const latestValues = (item) => {
  const rows = Array.isArray(item?.weeklySnapshots) ? item.weeklySnapshots : [];
  const sale = pickLatest(rows, ['salePrice']);            // 매매가(호가)
  const saleReal = pickLatest(rows, ['saleRealPrice']);   // 매매 실거래가
  const lease = pickLatest(rows, ['leasePrice']);          // 전세가(호가)
  const leaseReal = pickLatest(rows, ['leaseRealPrice']); // 전세 실거래가
  const saleCount = pickLatest(rows, ['saleCount']);       // 매매 매물수
  const leaseCount = pickLatest(rows, ['leaseCount']);     // 전세 매물수
  const saleForRate = sale ?? saleReal;
  const leaseForRate = lease ?? leaseReal;
  let rate = pickLatest(rows, ['leasePriceRate']);
  if (rate == null && saleForRate != null && leaseForRate != null && saleForRate > 0) {
    rate = (leaseForRate / saleForRate) * 100;
  }
  // 투자금 = 매매가 − 전세가 (갭). 호가가 없으면 실거래가로 계산.
  const invest = (sale != null && lease != null)
    ? (sale - lease)
    : ((saleReal != null && leaseReal != null) ? (saleReal - leaseReal) : null);
  return { sale, saleReal, lease, leaseReal, rate, saleCount, leaseCount, invest };
};

const gradeLabel = (item) => {
  const s = item?.sigunguGrade;
  const d = item?.dongGrade;
  const has = (v) => v != null && v !== '' && String(v) !== '99';
  if (!has(s) && !has(d)) return '-';
  return `${has(s) ? s : '-'} / ${has(d) ? d : '-'}`;
};

// 값 출처가 아직 정해지지 않은 항목은 '준비중'으로 표기 (추후 지정)
const Pending = () => <span className="summary-pending" style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>준비중</span>;
const countLabel = (v) => (v != null ? `${formatNumber(v)}개` : '-');
// 적현율(%) — RankingWorkspace formatRatioPercent 와 동일: 0/비유효는 '-'
const rateLabel = (v) => {
  const n = Number(v);
  return (Number.isFinite(n) && n !== 0) ? `${n.toFixed(1)}%` : '-';
};

export default function CompareSummaryTable({ items = [], colorByCode }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div className="ranking-compare-summary">
      <div className="ranking-compare-summary-title">단지 비교</div>
      <div className="ranking-compare-summary-scroll">
        <table className="ranking-compare-summary-table">
          <thead>
            <tr className="grp-row">
              <th className="grp grp-end" colSpan={6}>단지정보</th>
              <th className="grp grp-end" colSpan={4}>투자지표</th>
              <th className="grp grp-end" colSpan={5}>시세</th>
              <th className="grp" colSpan={3}>매물·공급</th>
            </tr>
            <tr>
              <th className="col-name">단지명</th>
              <th>지역<span className="th-sub">(생활권)</span></th>
              <th>급지</th>
              <th>연식</th>
              <th>세대수</th>
              <th className="grp-end">평형</th>
              <th>적현율</th>
              <th>투자금</th>
              <th>최고가</th>
              <th className="grp-end">적고가</th>
              <th>매매가</th>
              <th>전세가</th>
              <th>매매실거래가</th>
              <th>전세실거래가</th>
              <th className="grp-end">전세가율</th>
              <th>매매 매물수</th>
              <th>전세 매물수</th>
              <th>공급율</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const color = (colorByCode && colorByCode.get(item.compareKey)) || '#334155';
              const { sale, saleReal, lease, leaseReal, rate, saleCount, leaseCount, invest } = latestValues(item);
              const fullName = item.displayName || item.buildingName || item.naverDanjiCode || '';
              const shortName = fullName.length > 12 ? `${fullName.slice(0, 12)}…` : fullName;
              return (
                <tr key={item.compareKey}>
                  <td className="col-name">
                    <span className="summary-dot" style={{ backgroundColor: color }} />
                    <span className="summary-name" style={{ color }} title={fullName}>{shortName}</span>
                  </td>
                  <td className="col-region">{item.regionName || item.address || '-'}</td>
                  <td>{gradeLabel(item)}</td>
                  <td>{ageLabel(item.useApproveDate)}</td>
                  <td>{item.familyCnt != null ? formatNumber(item.familyCnt) : '-'}</td>
                  <td className="grp-end">{item.pyeongLabel || (item.planTypeName || '-')}</td>
                  <td className="num">{rateLabel(item.jeokHyunRate)}</td>
                  <td className="num">{fmtEok(invest)}</td>
                  <td className="num">{fmtEok(item.topPrice)}</td>
                  <td className="num grp-end">{fmtEok(item.properHighPrice)}</td>
                  <td className="num">{fmtEok(sale)}</td>
                  <td className="num">{fmtEok(lease)}</td>
                  <td className="num">{fmtEok(saleReal)}</td>
                  <td className="num">{fmtEok(leaseReal)}</td>
                  <td className="num grp-end">{rate != null ? `${rate.toFixed(1)}%` : '-'}</td>
                  <td className="num">{countLabel(saleCount)}</td>
                  <td className="num">{countLabel(leaseCount)}</td>
                  <td className="num"><Pending /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
